import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, laveriesApi } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Loading, ApiError, EmptyState } from '../components/ui/Loading';
import { Badge } from '../components/ui/Badge';
import { Modal, ConfirmModal } from '../components/ui/Modal';
import { Avatar } from '../components/ui/Avatar';
import { Pagination } from '../components/ui/Pagination';
import { useToast } from '../context/ToastContext';
import { formatPrice, formatPercent, formatDate, paginate, truncate } from '../utils/helpers';

// ─── Sous-composant : commandes en attente de réception (v2.4) ───────────────
const PendingReceptionBlock = ({ laverieId }) => {
  const { data: raw, loading, error } = useApi(
    () => laverieId ? adminApi.getLaveriePendingReception(laverieId) : Promise.resolve({ data: [] }),
    [laverieId]
  );
  const orders = raw?.data || [];

  if (loading) return (
    <div style={{ margin: '16px 0 4px', fontSize: 12, color: 'var(--text3)' }}>
      Chargement commandes en attente...
    </div>
  );

  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>
        Commandes en attente de réception
        {orders.length > 0 && (
          <span style={{ marginLeft: 8, background: 'var(--orange)', color: '#fff', borderRadius: 8, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>
            {orders.length}
          </span>
        )}
      </div>

      {error ? (
        <div style={{ fontSize: 12, color: 'var(--red)' }}>Erreur : {error}</div>
      ) : orders.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text3)', padding: '8px 0' }}>
          ✅ Aucune commande picked_up en attente de réception.
        </div>
      ) : (
        <>
          {orders.some(o => o.minutes_since_pickup > 60) && (
            <div className="inline-alert inline-alert-red" style={{ marginBottom: 10 }}>
              <span>⚠️</span>
              <div style={{ fontSize: 12 }}>
                {orders.filter(o => o.minutes_since_pickup > 60).length} commande(s) en retard (&gt;60 min depuis le pickup).
              </div>
            </div>
          )}
          <div className="table-wrap" style={{ border: '1px solid var(--border)', borderRadius: 8 }}>
            <table style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th>Commande</th>
                  <th>Client</th>
                  <th>Livreur</th>
                  <th>Pickup</th>
                  <th>Écoulé</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o, i) => (
                  <tr key={i}>
                    <td className="td-main" style={{ fontFamily: 'monospace', fontSize: 11 }}>{o.order_number}</td>
                    <td>{o.client_name}</td>
                    <td style={{ color: 'var(--text2)' }}>{o.livreur_name || '—'}</td>
                    <td style={{ color: 'var(--text3)' }}>
                      {o.pickup_actual_time
                        ? new Date(o.pickup_actual_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                        : '—'}
                    </td>
                    <td>
                      <span style={{ fontWeight: 700, color: o.minutes_since_pickup > 60 ? 'var(--red)' : o.minutes_since_pickup > 30 ? 'var(--orange)' : 'var(--text)' }}>
                        {o.minutes_since_pickup} min
                      </span>
                    </td>
                    <td>
                      {o.minutes_since_pickup > 60 && (
                        <Badge className="badge-red">⚠️ Retard</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
const DAY_LABELS = { monday:'Lun', tuesday:'Mar', wednesday:'Mer', thursday:'Jeu', friday:'Ven', saturday:'Sam', sunday:'Dim' };
const DEFAULT_HOURS = Object.fromEntries(
  DAYS.slice(0,6).map(d => [d, { open: '08:00', close: '20:00' }]).concat([['sunday','closed']])
);
const DEFAULT_FORM = { name:'', email:'', phone:'', category:'B', description:'', address:'', latitude:'', longitude:'', language:'fr' };

export const Laveries = () => {
  const toast = useToast();
  const navigate = useNavigate();

  // ── Fetch laveries list from /laveries (public endpoint returns active ones)
  // Pour l'admin on fetch /laveries, la réponse inclut toutes les laveries
  const { data: raw, loading, error, refetch, setData } = useApi(
    () => laveriesApi.list()
  );

  // ── Stats individuelles chargées à la demande
  const [statsCache, setStatsCache] = useState({});
  const [loadingStats, setLoadingStats] = useState(false);

  const [search,       setSearch]       = useState('');
  const [catFilter,    setCatFilter]    = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page,         setPage]         = useState(1);

  const [createModal, setCreateModal] = useState(false);
  const [form,        setForm]        = useState(DEFAULT_FORM);
  const [hours,       setHours]       = useState(DEFAULT_HOURS);
  const [saving,      setSaving]      = useState(false);
  const [createdInfo, setCreatedInfo] = useState(null); // v2.2 : afficher le mot de passe temporaire

  const [quotaModal, setQuotaModal] = useState(null);
  const [quota,      setQuota]      = useState({ quota_min_daily: '', quota_max_daily: '' });
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting,    setDeleting]    = useState(false);
  const [permanent,   setPermanent]   = useState(false);

  const [detailModal, setDetailModal] = useState(null);

  const laveries = raw?.data || [];

  const filtered = useMemo(() => laveries.filter(l => {
    const q = search.toLowerCase();
    const matchSearch  = !q || l.name?.toLowerCase().includes(q) || l.address?.toLowerCase().includes(q);
    const matchCat     = catFilter    === 'all' || l.category === catFilter;
    const matchStatus  = statusFilter === 'all' || l.status   === statusFilter;
    return matchSearch && matchCat && matchStatus;
  }), [laveries, search, catFilter, statusFilter]);

  const paged = paginate(filtered, page, 10);

  const openStats = async (laverie) => {
    setDetailModal(laverie);
    if (statsCache[laverie.laverie_id]) return;
    setLoadingStats(true);
    try {
      const res = await adminApi.getLaverieStats(laverie.laverie_id);
      setStatsCache(p => ({ ...p, [laverie.laverie_id]: res.data }));
    } catch {}
    setLoadingStats(false);
  };

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.phone) {
      toast.error('Remplissez les champs obligatoires (nom, email, téléphone).');
      return;
    }
    setSaving(true);
    try {
      const res = await adminApi.createLaverie({
        ...form,
        working_hours: hours,
        latitude:  Number(form.latitude)  || 0,
        longitude: Number(form.longitude) || 0,
      });
      // v2.2 : le backend génère un mot de passe temporaire et l'envoie par email
      const tmp = res.data?.temporary_password;
      setCreateModal(false);
      setForm(DEFAULT_FORM);
      setHours(DEFAULT_HOURS);
      refetch();
      if (tmp) {
        setCreatedInfo({ name: form.name, email: form.email, temporary_password: tmp });
      } else {
        toast.success(`Laverie "${form.name}" créée ✓ — Email avec mot de passe temporaire envoyé à ${form.email}`);
      }
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateQuota = async () => {
    if (!quota.quota_min_daily || !quota.quota_max_daily) {
      toast.error('Remplissez les deux quotas.');
      return;
    }
    setSaving(true);
    try {
      await adminApi.updateLaverieQuotas(quotaModal.laverie_id, {
        quota_min_daily: Number(quota.quota_min_daily),
        quota_max_daily: Number(quota.quota_max_daily),
      });
      setData(prev => ({
        ...prev,
        data: prev.data.map(l =>
          l.laverie_id === quotaModal.laverie_id
            ? { ...l, quota_min_daily: Number(quota.quota_min_daily), quota_max_daily: Number(quota.quota_max_daily) }
            : l
        ),
      }));
      toast.success('Quotas mis à jour ✓');
      setQuotaModal(null);
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await adminApi.deleteLaverie(deleteModal.laverie_id, permanent);
      if (permanent) {
        // Hard delete → retirer de la liste
        setData(prev => ({ ...prev, data: prev.data.filter(l => l.laverie_id !== deleteModal.laverie_id) }));
        toast.success(`Laverie "${deleteModal.name}" supprimée définitivement.`);
      } else {
        // Soft delete → marquer inactive dans la liste
        setData(prev => ({
          ...prev,
          data: prev.data.map(l =>
            l.laverie_id === deleteModal.laverie_id ? { ...l, is_active: false, status: 'inactive' } : l
          ),
        }));
        toast.success(`Laverie "${deleteModal.name}" désactivée. Les données sont préservées.`);
      }
      setDeleteModal(null);
      setPermanent(false);
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <Loading text="Chargement des laveries..." />;
  if (error)   return <ApiError message={error} onRetry={refetch} />;

  return (
    <div>
      <div className="filter-bar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input className="search-input" placeholder="Nom, adresse..."
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <select className="filter-select" value={catFilter} onChange={e => { setCatFilter(e.target.value); setPage(1); }}>
          <option value="all">Catégorie: Toutes</option>
          <option value="A">A (Premium)</option>
          <option value="B">B (Standard)</option>
        </select>
        <select className="filter-select" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="all">Statut: Tous</option>
          <option value="active">Actif</option>
          <option value="inactive">Inactif</option>
        </select>
        <span style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{filtered.length} laveries</span>
        <button className="btn btn-ghost btn-sm" onClick={refetch}>↺</button>
        <button className="btn btn-primary" onClick={() => setCreateModal(true)}>+ Nouvelle laverie</button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Laverie</th><th>Catégorie</th><th>Statut</th><th>Note</th><th>Commandes</th><th>Utilisation</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {paged.items.map(l => {
                const utilRate = l.quota_max_daily ? l.spots_taken / l.quota_max_daily : 0;
                return (
                  <tr key={l.laverie_id}>
                    <td>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <Avatar name={l.name} />
                        <div>
                          <div className="td-main">{l.name}</div>
                          <div className="td-sub">{truncate(l.address, 32)}</div>
                        </div>
                      </div>
                    </td>
                    <td><Badge className={l.category === 'A' ? 'badge-green' : 'badge-blue'}>Cat. {l.category}</Badge></td>
                    <td><Badge className={l.status === 'active' ? 'badge-green' : 'badge-red'}>{l.status === 'active' ? 'Actif' : 'Inactif'}</Badge></td>
                    <td>⭐ {l.average_rating} <span style={{ color: 'var(--text3)', fontSize: 11 }}>({l.total_reviews})</span></td>
                    <td>
                      <div>{l.spots_taken ?? '—'} / {l.spots_total ?? '—'}</div>
                      <div className="td-sub">aujourd'hui</div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div className="progress-bar" style={{ flex: 1, maxWidth: 60 }}>
                          <div className="progress-fill" style={{
                            width: `${Math.min(100, utilRate * 100)}%`,
                            background: utilRate > 0.8 ? 'var(--red)' : utilRate > 0.5 ? 'var(--orange)' : 'var(--accent)',
                          }} />
                        </div>
                        <span style={{ fontSize: 11, color: 'var(--text3)' }}>{Math.round(utilRate * 100)}%</span>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => openStats(l)}>Stats</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => {
                          setQuotaModal(l);
                          setQuota({ quota_min_daily: l.quota_min_daily ?? '', quota_max_daily: l.quota_max_daily ?? '' });
                        }}>Quotas</button>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => navigate('/services', { state: { laverie_id: l.laverie_id } })}
                          title="Gérer les services et prix"
                        >
                          🔧 Services
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => setDeleteModal(l)}
                          title="Supprimer cette laverie"
                        >🗑</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {paged.items.length === 0 && (
            <EmptyState icon="🏪" title="Aucune laverie" text="Créez la première laverie ou modifiez les filtres."
              action={<button className="btn btn-primary" onClick={() => setCreateModal(true)}>+ Nouvelle laverie</button>} />
          )}
        </div>
        {paged.total > 0 && <div style={{ padding: '0 16px 16px' }}><Pagination {...paged} onPage={setPage} /></div>}
      </div>

      {/* CREATE MODAL */}
      <Modal open={createModal} onClose={() => setCreateModal(false)} title="Créer une laverie" size="lg"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setCreateModal(false)}>Annuler</button>
          <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>
            {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '+ Créer la laverie'}
          </button>
        </>}
      >
        <div className="form-row">
          <div className="form-group"><label className="form-label">Nom *</label><input className="form-input" placeholder="Laverie Express" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></div>
          <div className="form-group"><label className="form-label">Catégorie *</label>
            <select className="form-select" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
              <option value="A">A — Clients entreprises</option>
              <option value="B">B — Clients particuliers</option>
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Email <span style={{color:'var(--red)'}}>*</span> <span style={{color:'var(--text3)',fontSize:10}}>(reçoit le mot de passe)</span></label><input className="form-input" type="email" placeholder="contact@laverie.ma" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} /></div>
          <div className="form-group"><label className="form-label">Téléphone *</label><input className="form-input" placeholder="+212524..." value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} /></div>
        </div>
        <div className="inline-alert inline-alert-blue" style={{ marginBottom: 16 }}>
          <span>📧</span>
          <div style={{ fontSize: 12 }}>
            <strong>v2.2 :</strong> Un mot de passe temporaire est généré automatiquement et envoyé par email.
            La laverie devra le changer lors de sa première connexion.
          </div>
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Langue de l'email</label>
            <select className="form-select" value={form.language} onChange={e => setForm(p => ({ ...p, language: e.target.value }))}>
              <option value="fr">Français</option><option value="ar">Arabe</option><option value="en">Anglais</option>
            </select>
          </div>
        </div>
        <div className="form-group"><label className="form-label">Adresse</label><input className="form-input" placeholder="Avenue Hassan II, Casablanca" value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} /></div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Latitude</label><input className="form-input" type="number" step="0.0001" placeholder="33.5731" value={form.latitude} onChange={e => setForm(p => ({ ...p, latitude: e.target.value }))} /></div>
          <div className="form-group"><label className="form-label">Longitude</label><input className="form-input" type="number" step="0.0001" placeholder="-7.5898" value={form.longitude} onChange={e => setForm(p => ({ ...p, longitude: e.target.value }))} /></div>
        </div>
        <div className="form-group"><label className="form-label">Description</label><textarea className="form-textarea" placeholder="Service rapide et professionnel..." value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></div>
        <div style={{ marginTop: 8 }}>
          <div className="form-label" style={{ marginBottom: 10 }}>Horaires d'ouverture</div>
          {DAYS.map(day => (
            <div key={day} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <span style={{ width: 32, fontSize: 12, color: 'var(--text2)' }}>{DAY_LABELS[day]}</span>
              {hours[day] === 'closed'
                ? <><span style={{ flex: 1, fontSize: 12, color: 'var(--text3)' }}>Fermé</span><button className="btn btn-secondary btn-sm" onClick={() => setHours(p => ({ ...p, [day]: { open: '08:00', close: '20:00' } }))}>Ouvrir</button></>
                : <>
                  <input className="form-input" type="time" style={{ flex: 1 }} value={hours[day]?.open}  onChange={e => setHours(p => ({ ...p, [day]: { ...p[day], open: e.target.value } }))} />
                  <span style={{ color: 'var(--text3)' }}>→</span>
                  <input className="form-input" type="time" style={{ flex: 1 }} value={hours[day]?.close} onChange={e => setHours(p => ({ ...p, [day]: { ...p[day], close: e.target.value } }))} />
                  <button className="btn btn-ghost btn-sm" onClick={() => setHours(p => ({ ...p, [day]: 'closed' }))}>✕</button>
                </>
              }
            </div>
          ))}
        </div>
      </Modal>

      {/* QUOTA MODAL */}
      <Modal open={!!quotaModal} onClose={() => setQuotaModal(null)} title={`Quotas — ${quotaModal?.name}`} size="sm"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setQuotaModal(null)}>Annuler</button>
          <button className="btn btn-primary" onClick={handleUpdateQuota} disabled={saving}>
            {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Mettre à jour'}
          </button>
        </>}
      >
        <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
          Volume minimum et maximum de commandes par jour pour cette laverie.
        </p>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Quota minimum / jour</label><input className="form-input" type="number" min="0" value={quota.quota_min_daily} onChange={e => setQuota(p => ({ ...p, quota_min_daily: e.target.value }))} /></div>
          <div className="form-group"><label className="form-label">Quota maximum / jour</label><input className="form-input" type="number" min="1" value={quota.quota_max_daily} onChange={e => setQuota(p => ({ ...p, quota_max_daily: e.target.value }))} /></div>
        </div>
      </Modal>

      {/* STATS MODAL */}
      <Modal open={!!detailModal} onClose={() => setDetailModal(null)} title={`Statistiques — ${detailModal?.name}`} size="md">
        {loadingStats
          ? <Loading text="Chargement des statistiques..." />
          : detailModal && (() => {
              const s = statsCache[detailModal.laverie_id];
              if (!s) return <div style={{ color: 'var(--text2)', fontSize: 13 }}>Statistiques non disponibles.</div>;
              return (
                <div>
                  <div className="grid-2" style={{ gap: 12, marginBottom: 20 }}>
                    {[
                      { l: 'Revenu total',       v: formatPrice(s.revenue?.total) },
                      { l: 'Commission payée',   v: formatPrice(s.revenue?.commission_paid) },
                      { l: 'Revenu net',         v: formatPrice(s.revenue?.net) },
                      { l: 'Note moyenne',       v: `⭐ ${s.average_rating}/5 (${s.total_reviews} avis)` },
                    ].map((k, i) => (
                      <div key={i} style={{ background: 'var(--bg3)', borderRadius: 10, padding: '12px 14px' }}>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>{k.l}</div>
                        <div style={{ fontWeight: 700, fontFamily: 'Syne' }}>{k.v}</div>
                      </div>
                    ))}
                  </div>
                  {[
                    { l: 'Total commandes',     v: s.orders?.total },
                    { l: 'Terminées',           v: s.orders?.completed },
                    { l: 'Annulées',            v: s.orders?.cancelled },
                    { l: 'Taux de complétion',  v: s.orders?.completion_rate },
                    { l: 'Quota min / max',     v: `${s.quotas?.min_daily} — ${s.quotas?.max_daily} / jour` },
                    { l: 'Commandes auj.',      v: `${s.quotas?.current_daily} (${Math.round(s.quotas?.utilization_rate * 100)}% capacité)` },
                    { l: 'Performance',         v: `${s.performance_points} pts` },
                  ].map((r, i) => (
                    <div key={i} className="info-row"><span className="info-label">{r.l}</span><span className="info-value">{r.v ?? '—'}</span></div>
                  ))}

                  {/* ── Commandes en attente de réception v2.4 ── */}
                  <PendingReceptionBlock laverieId={detailModal?.laverie_id} />
                </div>
              );
            })()
        }
      </Modal>

      {/* ── CRÉÉ AVEC SUCCÈS : afficher le mot de passe temporaire (v2.2) ──── */}
      <Modal open={!!createdInfo} onClose={() => setCreatedInfo(null)}
        title="✅ Laverie créée avec succès" size="sm"
        footer={<button className="btn btn-primary btn-full" onClick={() => setCreatedInfo(null)}>Fermer</button>}
      >
        {createdInfo && (
          <div>
            <div className="inline-alert inline-alert-green" style={{ marginBottom: 16 }}>
              <span>✅</span>
              <div>
                La laverie <strong>{createdInfo.name}</strong> a été créée.
                Un email a été envoyé à <strong>{createdInfo.email}</strong>.
              </div>
            </div>
            <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: '14px 16px', marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                🔑 Mot de passe temporaire
              </div>
              <div style={{
                fontFamily: 'monospace', fontSize: 22, fontWeight: 800, letterSpacing: 3,
                color: 'var(--accent)', userSelect: 'all', cursor: 'text',
                textAlign: 'center', padding: '8px 0',
              }}>
                {createdInfo.temporary_password}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center', marginTop: 6 }}>
                Cliquez pour sélectionner et copier
              </div>
            </div>
            <div className="inline-alert inline-alert-yellow">
              <span>⚠️</span>
              <div style={{ fontSize: 12 }}>
                La laverie devra <strong>changer ce mot de passe</strong> lors de sa première connexion.
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ── SUPPRESSION LAVERIE ── */}
      <Modal
        open={!!deleteModal}
        onClose={() => { setDeleteModal(null); setPermanent(false); }}
        title={`Supprimer — ${deleteModal?.name}`}
        size="sm"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => { setDeleteModal(null); setPermanent(false); }} disabled={deleting}>
              Annuler
            </button>
            <button
              className={`btn ${permanent ? 'btn-danger' : 'btn-secondary'}`}
              onClick={handleDelete}
              disabled={deleting}
              style={permanent ? {} : { borderColor: 'var(--orange)', color: 'var(--orange)', background: 'rgba(240,160,74,0.08)' }}
            >
              {deleting
                ? <><span className="spinner" style={{ width: 14, height: 14 }} /> En cours...</>
                : permanent ? '🗑 Supprimer définitivement' : '⏸ Désactiver'}
            </button>
          </>
        }
      >
        {deleteModal && (
          <div>
            <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
              Choisissez le type de suppression pour <strong>{deleteModal.name}</strong> :
            </p>

            {/* Option soft */}
            <div
              onClick={() => setPermanent(false)}
              style={{
                border: `2px solid ${!permanent ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 10,
                padding: '12px 14px',
                marginBottom: 10,
                cursor: 'pointer',
                background: !permanent ? 'rgba(232,240,74,0.05)' : 'var(--bg3)',
                transition: 'all 0.15s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
                <span style={{ fontSize: 20 }}>⏸</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: !permanent ? 'var(--accent)' : 'var(--text)' }}>
                    Désactivation (recommandé)
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>Soft delete · Réversible</div>
                </div>
                {!permanent && <span style={{ marginLeft: 'auto', color: 'var(--accent)', fontWeight: 700 }}>✓</span>}
              </div>
              <ul style={{ fontSize: 12, color: 'var(--text2)', paddingLeft: 16, lineHeight: 1.8, margin: 0 }}>
                <li>Compte désactivé (<code>is_active = false</code>, <code>status = inactive</code>)</li>
                <li>Toutes les données et l'historique sont préservés</li>
                <li>Notification envoyée à la laverie</li>
                <li>Réactivable à tout moment depuis la liste</li>
              </ul>
            </div>

            {/* Option hard */}
            <div
              onClick={() => setPermanent(true)}
              style={{
                border: `2px solid ${permanent ? 'var(--red)' : 'var(--border)'}`,
                borderRadius: 10,
                padding: '12px 14px',
                cursor: 'pointer',
                background: permanent ? 'rgba(240,74,122,0.05)' : 'var(--bg3)',
                transition: 'all 0.15s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
                <span style={{ fontSize: 20 }}>🗑</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: permanent ? 'var(--red)' : 'var(--text)' }}>
                    Suppression définitive
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>Hard delete · IRRÉVERSIBLE</div>
                </div>
                {permanent && <span style={{ marginLeft: 'auto', color: 'var(--red)', fontWeight: 700 }}>✓</span>}
              </div>
              <ul style={{ fontSize: 12, color: 'var(--text2)', paddingLeft: 16, lineHeight: 1.8, margin: 0 }}>
                <li>Suppression complète de la base de données</li>
                <li>Services, assignations, notifications supprimés</li>
                <li>Commandes historiques anonymisées (préservées)</li>
                <li style={{ color: 'var(--red)', fontWeight: 600 }}>⚠️ Action irréversible — impossible à annuler</li>
              </ul>
            </div>

            {permanent && (
              <div className="inline-alert inline-alert-red" style={{ marginTop: 12 }}>
                <span>⚠️</span>
                <span>Vous êtes sur le point de <strong>supprimer définitivement</strong> cette laverie. Cette action ne peut pas être annulée.</span>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};